import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import PromptInput from '@amzn/awsui-components-console/prompt-input';
import { useChatbotLogic } from './useChatbotLogic';
import styles from './ChatbotWidget.module.scss';
import './ChatbotWidget.scss';
import { SnowmanIcon } from './SnowmanIcon';
import { WelcomeCard } from './WelcomeCard';
import { HelpSection } from './HelpSection';
import { ResizeHandle } from './ResizeHandle';
import { ASSISTANT_DISPLAY_NAME, INPUT_PLACEHOLDER } from './constants';
import { ChatHeader } from './ChatHeader';
import { ChatSidebar } from './ChatSidebar';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import { MessageList } from './MessageList';

const ChatbotWidgetInner: React.FC<RouteComponentProps> = ({ history }) => {
	const {
		isIconHovered,
		isOpen,
		messages,
		inputValue,
		isTyping,
		showSuggestions,
		currentView,
		sidebarOpen,
		buttonPosition,
		isDragging,
		chatPanelRef,
		messagesEndRef,
		suggestions,
		userPhotoUrl,
		username,
		panelWidth,
		panelPositionStyle,
		showWelcome,
		showScrollToBottom,
		setIsIconHovered,
		setIsOpen,
		setInputValue,
		setCurrentView,
		setSidebarOpen,
		handleSendMessage,
		handleSuggestionClick,
		handleNewConversation,
		toggleOpen,
		handleMouseDown,
		handlePanelResize,
		handlePanelResizeEnd,
		scrollToBottom,
		messageListRef,
		stopResponse,
	} = useChatbotLogic();

	const promptItems = suggestions.map((text) => ({ text, id: text }));

	const handleExpand = () => {
		setIsOpen(false);
		history.push('/chat');
	};

	return (
		<div className={styles.widget}>
			{/* Floating button */}
			<button
				className={`${styles.floatingIcon} chatbot-floating-icon ${isTyping ? 'typing' : ''} ${isIconHovered ? styles.floatingIconHover : ''}`}
				style={{ bottom: `${buttonPosition}px`, cursor: isDragging ? 'grabbing' : 'grab' }}
				onMouseEnter={() => setIsIconHovered(true)}
				onMouseLeave={() => setIsIconHovered(false)}
				onMouseDown={handleMouseDown}
				onClick={() => { if (!isDragging) toggleOpen(); }}
				aria-label="Open chatbot"
				title={`Chat with ${ASSISTANT_DISPLAY_NAME} (Drag to move)`}
			>
				<SnowmanIcon size={38} />
			</button>

			{isOpen && (
				<div
					className={`${styles.panel} chatbot-panel-enter`}
					style={{ ...panelPositionStyle, width: `${panelWidth}px` }}
					ref={chatPanelRef}
				>
					<ResizeHandle edge="top" onResize={handlePanelResize} onResizeEnd={handlePanelResizeEnd} />
					<ResizeHandle edge="left" onResize={handlePanelResize} onResizeEnd={handlePanelResizeEnd} />

					<ChatHeader
						sidebarOpen={sidebarOpen}
						setSidebarOpen={setSidebarOpen}
						toggleOpen={toggleOpen}
						onExpand={handleExpand}
					/>

					<div className={styles.panelBody}>
						{sidebarOpen && (
							<ChatSidebar
								currentView={currentView}
								setSidebarOpen={setSidebarOpen}
								setCurrentView={setCurrentView}
								handleNewConversation={handleNewConversation}
							/>
						)}

						<div className={styles.panelContent}>
							{currentView === 'help' ? (
								<HelpSection
									onBack={() => setCurrentView('chat')}
									onPromptClick={(text) => { handleSuggestionClick(text); setCurrentView('chat'); }}
								/>
							) : (
								<>
									<div className={styles.messageList} ref={messageListRef}>
										<div className={styles.messagesContainer}>
											{showWelcome ? (
												<WelcomeCard
													onSuggestionClick={handleSuggestionClick}
													suggestions={suggestions}
												/>
											) : (
												<MessageList
													messages={messages}
													isTyping={isTyping}
													showSuggestions={showSuggestions}
													showWelcome={showWelcome}
													username={username}
													userPhotoUrl={userPhotoUrl}
													promptItems={promptItems}
													messagesEndRef={messagesEndRef}
													handleSuggestionClick={handleSuggestionClick}
												/>
											)}

											<div ref={messagesEndRef} />
										</div>
									</div>

									{showScrollToBottom && !showWelcome && (
										<ScrollToBottomButton onClick={scrollToBottom} />
									)}

									<div className={styles.messageInput}>
										<div style={{ width: '100%' }}>
											<PromptInput
												value={inputValue}
												onChange={({ detail }) => setInputValue(detail.value)}
												onAction={isTyping ? stopResponse : handleSendMessage}
												actionButtonIconName={isTyping ? 'stop-circle' : 'send'}
												i18nStrings={{ actionButtonAriaLabel: isTyping ? 'Stop responding' : 'Send message' }}
												ariaLabel="Message input"
												data-testid="chatbot-input"
												maxRows={6}
												minRows={1}
												placeholder={INPUT_PLACEHOLDER}
												disableActionButton={!isTyping && !inputValue.trim()}
											/>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

/** Guard component — handles route check without calling hooks conditionally */
const ChatbotWidget: React.FC<RouteComponentProps> = (props) => {
	if (props.location.pathname === '/chat') return null;

	return <ChatbotWidgetInner {...props} />;
};

export default withRouter(ChatbotWidget);
